import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const demoAccounts = [
  { label: 'Admin',   email: 'admin@school.com',   password: 'admin@123',   color: 'bg-blue-100 text-blue-800' },
  { label: 'Teacher', email: 'teacher@school.com',  password: 'teacher@123', color: 'bg-green-100 text-green-800' },
  { label: 'Student', email: 'student@school.com',  password: 'student@123', color: 'bg-yellow-100 text-yellow-800' },
  { label: 'Parent',  email: 'parent@school.com',   password: 'parent@123',  color: 'bg-purple-100 text-purple-800' },
];

function LoginPage() {
  const [email, setEmail] = useState('admin@school.com');
  const [password, setPassword] = useState('admin@123');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (result.success) navigate('/dashboard');
    else setError(result.error || 'Invalid email or password');
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col items-center justify-center flex-1 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 px-12 text-center">
        <img src="/school-logo.png" alt="Vasavya Vidyalayam" className="w-48 mb-8 drop-shadow-xl" onError={e => e.target.style.display='none'} />
        <h1 className="text-4xl font-extrabold text-white tracking-tight">VASAVYA</h1>
        <h2 className="text-2xl font-bold text-green-400 mt-1st">VIDYALAYAM</h2>
        <p className="text-blue-300 mt-4 text-sm max-w-xs leading-relaxed">
          Empowering education through smart school management
        </p>
        <div className="mt-10 grid grid-cols-2 gap-4 text-center text-white text-sm w-full max-w-xs">
          {[['Students', '450+'], ['Teachers', '32'], ['Classes', '18'], ['Years', '15+']].map(([label, val]) => (
            <div key={label} className="bg-white/10 rounded-xl py-3 px-4 backdrop-blur">
              <div className="text-2xl font-bold text-green-400">{val}</div>
              <div className="text-blue-200 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <img src="/school-logo.png" alt="Vasavya Vidyalayam" className="w-32 mb-3" onError={e => e.target.style.display='none'} />
            <h1 className="text-2xl font-extrabold text-blue-900">VASAVYA</h1>
            <h2 className="text-base font-bold text-green-600st">VIDYALAYAM</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-xl font-bold mb-1">Welcome back</h2>
            <p className="text-sm mb-6">Sign in to your account to continue</p>

            {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-1 rounded-lg mb-4 border border-red-100">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Enter your email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 transition"
                    placeholder="Enter your password" />
                  <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-gray-600">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#1e3a8a' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1e40af'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e3a8a'}>
                <LogIn size={18} />{loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-gray-100">
              <p className="mb-3 text-center font-medium">Quick Demo Login</p>
              <div className="grid grid-cols-2 gap-2">
                {demoAccounts.map(acc => (
                  <button key={acc.label} onClick={() => { setEmail(acc.email); setPassword(acc.password); }}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80 ${acc.color}`}>
                    {acc.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default LoginPage;
